import { useState } from "react";
import { toast } from "sonner";
import { BellPlus, Loader2 } from "lucide-react";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export const FREQ_OPTIONS = [
  { id: "instant", label: "ss_instant", sub: "ss_instant_sub" },
  { id: "daily", label: "ss_daily", sub: "ss_daily_sub" },
];

export const FrequencyPicker = ({ value, onChange, testid = "ss-freq" }) => {
  const { t } = useLanguage();
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={testid}>
      {FREQ_OPTIONS.map((o) => (
        <button key={o.id} type="button" onClick={() => onChange(o.id)} data-testid={`${testid}-${o.id}`}
          className={`text-left rounded-xl border-2 px-3 py-2.5 transition-colors ${value === o.id ? "border-[#F5551E] bg-[#FFF4F0]" : "border-slate-200 hover:border-slate-300"}`}>
          <p className="text-sm font-semibold text-[#12234A]">{t(o.label)}</p>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{t(o.sub)}</p>
        </button>
      ))}
    </div>
  );
};

export const SaveSearchButton = ({ filters }) => {
  const { user, setAuthOpen } = useAuth();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [freq, setFreq] = useState("instant");
  const [saving, setSaving] = useState(false);

  const start = () => (user ? (setName(""), setFreq("instant"), setOpen(true)) : setAuthOpen(true));
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/saved-searches", { name, filters, frequency: freq });
      toast.success(t("ss_saved"));
      setOpen(false);
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button onClick={start} variant="outline" data-testid="save-search-btn" className="h-10 rounded-xl gap-1.5 border-[#F5551E]/40 text-[#F5551E] hover:bg-[#FFF4F0] bg-white">
        <BellPlus size={16} /> {t("ss_save")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="save-search-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><BellPlus size={18} className="text-[#F5551E]" /> {t("ss_title")}</DialogTitle>
            <DialogDescription className="text-slate-500">{t("ss_how")}</DialogDescription>
          </DialogHeader>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder={t("ss_name")} data-testid="save-search-name" className="rounded-xl" />
          <FrequencyPicker value={freq} onChange={setFreq} />
          <Button onClick={save} disabled={saving} data-testid="save-search-confirm" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5">
            {saving && <Loader2 size={15} className="animate-spin" />} {t("ss_save")}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
